import type { CkbfsRef } from "@manifest/schema.ts";
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

// Allow `className` on <code> and <span> so syntax highlighting and KaTeX
// rendering can attach their styling classes after sanitization.
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code ?? []), ["className"]],
    span: [...(defaultSchema.attributes?.span ?? []), ["className"]],
  },
};

interface MarkdownProps {
  children: string;
  imageRefs?: Record<string, CkbfsRef>;
  fetchBytes?: (ref: CkbfsRef) => Promise<Uint8Array>;
}

export function Markdown({ children, imageRefs, fetchBytes }: MarkdownProps) {
  const components = useMemo(() => {
    if (!imageRefs || !fetchBytes) return undefined;
    return {
      img: (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
        <CkbfsImage {...props} imageRefs={imageRefs} fetchBytes={fetchBytes} />
      ),
    };
  }, [imageRefs, fetchBytes]);

  return (
    <div
      className={[
        "prose max-w-none break-words",
        // Parchment-tuned typography — warm body text, dark headings, and
        // softly-tinted code surfaces so blocks read as part of the page
        // rather than islands.
        "prose-headings:font-serif prose-headings:text-parchment-900",
        "prose-p:text-parchment-800 prose-li:text-parchment-800",
        "prose-strong:text-parchment-900",
        "prose-a:text-parchment-800 prose-a:decoration-parchment-400 hover:prose-a:decoration-parchment-700",
        "prose-blockquote:text-parchment-700 prose-blockquote:border-parchment-400",
        "prose-code:text-parchment-800 prose-code:bg-parchment-50 prose-code:rounded prose-code:px-1",
        "prose-pre:bg-parchment-50 prose-pre:text-parchment-800 prose-pre:border prose-pre:border-parchment-300",
        "prose-hr:border-parchment-400",
        "prose-th:text-parchment-900 prose-td:text-parchment-800",
        "[&_pre]:overflow-x-auto [&_pre]:max-w-full",
        "[&_table]:block [&_table]:overflow-x-auto [&_table]:max-w-full",
        "[&_img]:max-w-full",
      ].join(" ")}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema], rehypeHighlight, rehypeKatex]}
        {...(components ? { components } : {})}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

interface CkbfsImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  imageRefs: Record<string, CkbfsRef>;
  fetchBytes: (ref: CkbfsRef) => Promise<Uint8Array>;
}

/**
 * Resolves a markdown <img src> through the per-doc image map and
 * renders the bytes as a blob URL. Refs not present in the map fall
 * through to the original src (covers absolute https URLs that were
 * intentionally skipped during corpus fetch / mint).
 *
 * Sanitization runs at the HAST stage on the original (relative) src,
 * so swapping in a blob: URL inside this component bypasses the
 * sanitizer's protocol allowlist by design — the sanitizer never sees
 * blob: URLs from us.
 */
function CkbfsImage({ src, alt, title, imageRefs, fetchBytes }: CkbfsImageProps) {
  const ref = typeof src === "string" ? imageRefs[src] : undefined;
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!ref) return;
    let cancelled = false;
    let url: string | undefined;
    fetchBytes(ref).then(
      (bytes) => {
        if (cancelled) return;
        // Cast: TS 5.7's Uint8Array<ArrayBufferLike> isn't assignable to
        // Blob's BufferSource (which expects ArrayBuffer specifically).
        // The runtime accepts both fine.
        const blob = new Blob([bytes as BlobPart], { type: "image/avif" });
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      },
      () => {
        // Swallow errors here; the placeholder remains visible so the
        // reader can scroll past a missing figure rather than crash.
      },
    );
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [ref, fetchBytes]);

  if (!ref) {
    return <img src={src} alt={alt ?? ""} title={title} />;
  }
  if (!blobUrl) {
    return (
      <span className="block text-parchment-600/70 italic text-sm py-4">
        loading image{alt ? `: ${alt}` : "…"}
      </span>
    );
  }
  return <img src={blobUrl} alt={alt ?? ""} title={title} />;
}
